import React, { Fragment, useMemo, useState } from 'react'
import BaseModal from 'components/CommonComponents/BaseModal'
import { Box, Button, Divider, Flex, IconButton, RadioGroup, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { BsArrowLeftShort } from 'react-icons/bs'
import { CustomRadio } from 'components/CommonComponents/AudiencePublicationModal/AudienceOptionItem'
import { Campaign } from 'redux/reducers/campaigns'
import { CAMPAIGN_STATUS_TYPES } from 'components/AdminConsole/Competitions/utils'

interface IProps {
  isOpen: boolean
  closeModal: () => void
  selectedCampaignLink: string | null
  setSelectedCampaignLink: (value: string) => void
  setSelectedTaskLink: (value: string) => void
  setSelectedChatLink: (value: string) => void
  setSelectedKpiLink: (value: string) => void
  campaigns: Campaign[]
}

export const CampaignsLinksModal = ({
  isOpen,
  closeModal,
  selectedCampaignLink,
  setSelectedCampaignLink,
  setSelectedTaskLink,
  setSelectedChatLink,
  setSelectedKpiLink,
  campaigns,
}: IProps) => {
  const isRtl = useSelector(isRtlSelector)
  const theme = useSelector((state: RootState) => state.general.theme)

  const [value, setValue] = useState(selectedCampaignLink ?? '')

  const sortedCampaigns = useMemo(() => {
    return [...(campaigns ?? [])]
      .filter((campaign) => campaign.status === CAMPAIGN_STATUS_TYPES.ACTIVE)
      .sort((a, b) => a.title.localeCompare(b.title))
  }, [campaigns])

  const handleSubmit = () => {
    closeModal()
    setSelectedCampaignLink(value)
    setSelectedTaskLink('')
    setSelectedChatLink('')
    setSelectedKpiLink('')
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value.toString())
  }

  const cancelSelection = () => {
    setValue('')
  }

  return (
    <BaseModal
      isShowAnimation={true}
      isOpen={isOpen}
      onClose={closeModal}
      hideCloseButton={true}
      overflowY="clip"
      headerContent={
        <>
          <Flex alignItems="center" justifyContent="space-between" dir={isRtl ? 'rtl' : 'ltr'}>
            <Box onClick={closeModal} mx="4" position="absolute">
              <IconButton
                aria-label="close groups"
                icon={BsArrowLeftShort}
                borderRadius="50%"
                style={{ color: colors.greyMain }}
                fontSize="32px"
                transform={`rotate(${+isRtl * 180}deg)`}
              />
            </Box>

            <Text m="0" textAlign="center" fontWeight="bold" flexGrow={1}>
              <LocaleText text="link_campaign" />
            </Text>
          </Flex>
          <Divider pt="5px" />
        </>
      }
      bodyContent={
        <RadioGroup value={value} px="25px" mt="20px">
          {sortedCampaigns &&
            sortedCampaigns.map((campaign, index: number) => (
              <Fragment key={index}>
                <CustomRadio
                  onChange={handleSelect}
                  isChecked={value === campaign?.oid?.toString()}
                  w="100%"
                  justifyContent="space-between"
                  flexDir="row-reverse"
                  value={campaign?.oid?.toString()}
                  size="lg"
                  color={theme?.elementsColor}
                  key={index}
                >
                  <Flex flexDir="row" alignItems="center" color={colors.blackLight}>
                    <Text mx="10px">
                      <LocaleText text={campaign.title || ''} />
                    </Text>
                  </Flex>
                </CustomRadio>
                <Divider pt="5px" />
              </Fragment>
            ))}
        </RadioGroup>
      }
      footerContent={
        <>
          <Flex justifyContent="space-between" background="white" px="6" py={5} w="full">
            <Button
              borderRadius="20px"
              onClick={handleSubmit}
              color="white"
              textTransform="uppercase"
              background={theme?.elementsColor}
              _hover={{ backgroundColor: 'none' }}
            >
              <LocaleText text="post_audience_done" />
            </Button>
            <Button
              variant="outline"
              borderRadius="20px"
              onClick={cancelSelection}
              color={colors.tomato}
              textTransform="uppercase"
              background={colors.tealLight}
              _hover={{ backgroundColor: 'none' }}
            >
              <LocaleText text="cancel" />
            </Button>
          </Flex>
        </>
      }
    />
  )
}
